import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { locationsCountriesAtom } from 'src/atoms/filters/locations';
import { CheckboxStyled } from 'src/common/components/CheckboxStyled/CheckboxStyled';
import { useGetCountryList } from 'src/locations/hooks/useGetCountryList';

const StyledAutocomplete = styled(Autocomplete)(
  {
    '.MuiAutocomplete-inputRoot': { height: 'auto' },
  },
  {},
) as typeof Autocomplete;

const DisabledCountryDropdown: FC = () => {
  const { t } = useTranslation();

  return (
    <StyledAutocomplete
      data-testid="locations.filters.country.dropdown"
      id="country-select"
      multiple
      options={[]}
      disabled
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                'data-testid': 'locations.filters.country.dropdown.input',
              },
            }}
            placeholder={t('locations.map.selectAnAccount')}
          />
        );
      }}
    />
  );
};

type CountryDropdownProps = {
  accountReferenceId: string;
};

const CountryDropdown: FC<CountryDropdownProps> = ({ accountReferenceId }) => {
  const { t } = useTranslation();
  const [countries, setCountries] = useAtom(locationsCountriesAtom);

  const { data: countryOptions, status } = useGetCountryList({
    accountReferenceId,
  });

  if (status === 'pending') {
    return <Skeleton />;
  }

  if (status === 'error') {
    return (
      <Typography variant="text2" color="error">
        {t('common.somethingWrong')}
      </Typography>
    );
  }

  const selectedCountries = countryOptions.filter(
    (o) => o.countryCode && countries.includes(o.countryCode),
  );

  return (
    <StyledAutocomplete
      data-testid="locations.filters.country.dropdown"
      id="country-select"
      limitTags={3}
      multiple
      options={countryOptions}
      disableCloseOnSelect
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                'data-testid': 'locations.filters.country.dropdown.input',
              },
            }}
            placeholder={
              countries.length > 0 ? undefined : t('locations.filters.country.placeholder')
            }
          />
        );
      }}
      slotProps={{
        listbox: {
          // @ts-expect-error data-testid is a valid HTML attribute
          'data-testid': 'locations.filters.country.dropdown.listbox',
        },
      }}
      renderOption={(props, option, state) => {
        const { key, ...restProps } = props;
        const { selected } = state;
        return (
          <li
            key={key}
            data-testid={`locations.filters.country.dropdown.option.${option.countryCode}`}
            {...restProps}
          >
            <CheckboxStyled isChecked={selected} onChange={() => {}} />
            {option.countryName}
          </li>
        );
      }}
      getOptionLabel={(o) => o.countryName!}
      onChange={(_e, newValues) => {
        const newCountryCodes = newValues.map((o) => o.countryCode!);
        setCountries(newCountryCodes);
      }}
      value={selectedCountries}
    />
  );
};

type LocationsFiltersCountryProps = { accountReferenceId: string | null };

export const LocationsFiltersCountry: FC<LocationsFiltersCountryProps> = (props) => {
  const { accountReferenceId } = props;

  const { t } = useTranslation();

  return (
    <Stack
      flexBasis="content"
      flexGrow="1"
      alignItems="center"
      direction="row"
      useFlexGap
      spacing={16}
      divider={<Divider orientation="vertical" flexItem />}
    >
      <Stack alignItems="center" direction="row" useFlexGap spacing={10} flexBasis="content">
        <Typography
          data-testid="locations.filters.country.label"
          variant="text2"
          color="textSecondary"
        >
          {t('locations.filters.country.label')}
        </Typography>
        <Box
          sx={{
            minWidth: '29ch',
          }}
        >
          {!accountReferenceId ? <DisabledCountryDropdown /> : null}
          {accountReferenceId ? <CountryDropdown accountReferenceId={accountReferenceId} /> : null}
        </Box>
      </Stack>
    </Stack>
  );
};
